import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertOctagon, CalendarDays, ClipboardList, FileText, History,
  StickyNote, Wrench, ArrowRightLeft,
} from "lucide-react";
import { api } from "../../lib/api";

export function VehicleTimelineTab({ vehicleId }) {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get(`/vehicles/${vehicleId}/timeline?limit=300`)
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [], count: 0 }));
  }, [vehicleId]);

  if (data === null) return <div className="text-sm text-slate-500 p-6">Loading…</div>;

  const FILTERS = [
    { id: "all", label: "All" },
    { id: "booking", label: "Bookings" },
    { id: "damage", label: "Damages" },
    { id: "incident", label: "Incidents" },
    { id: "inspection", label: "Inspections" },
    { id: "maintenance", label: "Maintenance" },
    { id: "status", label: "Status changes" },
    { id: "note", label: "Notes" },
    { id: "document", label: "Documents" },
    { id: "audit", label: "System log" },
  ];

  const items = (data.items || []).filter((e) => filter === "all" || e.kind === filter);

  // Group by day
  const byDay = {};
  for (const e of items) {
    const day = (e.at || "").slice(0, 10) || "-";
    (byDay[day] ||= []).push(e);
  }
  const days = Object.keys(byDay).sort().reverse();

  return (
    <div className="space-y-4" data-testid="vd-timeline">
      <div className="rounded-xl border border-slate-200 bg-white p-4 flex flex-wrap gap-2" data-testid="vd-timeline-filters">
        {FILTERS.map((f) => {
          const count = f.id === "all"
            ? (data.items || []).length
            : (data.items || []).filter((e) => e.kind === f.id).length;
          return (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`vd-timeline-filter-${f.id}`}
              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition ${
                filter === f.id
                  ? "bg-[#1B4332] text-white border-[#1B4332]"
                  : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
              }`}
            >
              {f.label} <span className="opacity-70">({count})</span>
            </button>
          );
        })}
      </div>

      {items.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500" data-testid="vd-timeline-empty">
          No timeline events yet for this filter.
        </div>
      ) : (
        days.map((day) => (
          <div key={day} className="rounded-xl border border-slate-200 bg-white p-6" data-testid={`vd-timeline-day-${day}`}>
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              {day === "-" ? "Earlier" : new Date(day).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
            </div>
            <ol className="mt-3 space-y-3">
              {byDay[day].map((e, idx) => (
                <TimelineRow key={`${e.kind}-${idx}`} event={e} />
              ))}
            </ol>
          </div>
        ))
      )}
    </div>
  );
}

function TimelineRow({ event }) {
  const KIND_META = {
    booking:     { color: "border-sky-300 bg-sky-50 text-sky-900", icon: CalendarDays },
    damage:      { color: "border-amber-300 bg-amber-50 text-amber-900", icon: AlertOctagon },
    incident:    { color: "border-rose-300 bg-rose-50 text-rose-900", icon: AlertOctagon },
    inspection:  { color: "border-emerald-300 bg-emerald-50 text-emerald-900", icon: ClipboardList },
    maintenance: { color: "border-violet-300 bg-violet-50 text-violet-900", icon: Wrench },
    status:      { color: "border-teal-300 bg-teal-50 text-teal-900", icon: ArrowRightLeft },
    note:        { color: "border-slate-300 bg-slate-50 text-slate-900", icon: StickyNote },
    document:    { color: "border-indigo-300 bg-indigo-50 text-indigo-900", icon: FileText },
    audit:       { color: "border-slate-200 bg-white text-slate-700", icon: History },
  };
  const meta = KIND_META[event.kind] || KIND_META.audit;
  const Icon = meta.icon;
  const time = event.at ? new Date(event.at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : "";

  const content = (
    <div className={`rounded-lg border p-3 ${meta.color}`}>
      <div className="flex items-start gap-2">
        <Icon className="w-4 h-4 mt-0.5 opacity-80 flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="font-medium capitalize text-sm">{event.title}</div>
            <span className="text-[11px] opacity-70 whitespace-nowrap">{time}</span>
          </div>
          {event.description && (
            <div className="text-xs opacity-80 mt-0.5 whitespace-pre-line line-clamp-3">{event.description}</div>
          )}
          {event.meta?.by && (
            <div className="text-[10px] opacity-60 mt-0.5">by {event.meta.by}</div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <li data-testid={`vd-timeline-row-${event.kind}`}>
      {event.link ? <Link to={event.link} className="block hover:opacity-90">{content}</Link> : content}
    </li>
  );
}
